import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { ArrowLeft, FileJson, Plus, Save, ShieldCheck, Trash2 } from 'lucide-react';

import { PREMIUM_PLAN_NAME, UPGRADE_ROUTE, isPremiumTier } from '../config/entitlements';
import { apiFetch } from '../utils/apiFetch';
import { resolveTierFromUser } from '../utils/runtimeTier';
import './RulesetEditor.css';

const SECTIONS = [
  { id: 'foundation', label: 'Foundation' },
  { id: 'attacks', label: 'Attacks' },
  { id: 'magic', label: 'Magic' },
  { id: 'grapple-movement', label: 'Grapple & Movement' },
  { id: 'rests', label: 'Resting' },
  { id: 'overlays', label: 'Rule Overlays' },
  { id: 'source-sharing', label: 'Source & Sharing' }
];

// The editor saves append-only executable versions. Source prose is never a
// runtime rules fallback, and every new package begins private.
// date_of_change: 2026-08-21

const clone = (value) => JSON.parse(JSON.stringify(value));

const nextPatchVersion = (value) => {
  const parts = String(value || '').split('.').map(Number);
  return parts.length === 3 && parts.every(Number.isInteger)
    ? `${parts[0]}.${parts[1]}.${parts[2] + 1}`
    : value;
};

const getAtPath = (value, path) => path.reduce((current, key) => current?.[key], value);

const setAtPath = (value, path, nextValue) => {
  const copy = clone(value);
  let current = copy;
  path.slice(0, -1).forEach((key) => {
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) current[key] = {};
    current = current[key];
  });
  current[path[path.length - 1]] = nextValue;
  return copy;
};

const rulesetDefinitionPayload = (draft) => ({
  schemaVersion: draft.schemaVersion,
  name: draft.name,
  description: draft.description,
  version: draft.version,
  edition: draft.edition,
  baseRules: draft.baseRules,
  modules: draft.modules,
  overlays: draft.overlays
});

const rulesetPayload = (draft) => ({
  ...rulesetDefinitionPayload(draft),
  visibility: draft.visibility,
  provenance: draft.provenance
});

const readErrorPayload = async (response) => {
  try {
    const payload = await response.json();
    return {
      message: payload?.message || payload?.error?.message || `The ruleset could not be saved (${response.status}).`,
      issues: payload?.issues || payload?.error?.issues || []
    };
  } catch (_error) {
    return { message: `The ruleset could not be saved (${response.status}).`, issues: [] };
  }
};

const Field = ({ label, help, children }) => (
  <label className="ruleset-editor__field">
    <span className="ruleset-editor__field-label">{label}</span>
    {children}
    {help ? <span className="ruleset-editor__field-help">{help}</span> : null}
  </label>
);

const ToggleField = ({ label, help, checked, disabled, onChange }) => (
  <label className="ruleset-editor__toggle-row">
    <span>
      <span className="ruleset-editor__field-label">{label}</span>
      <span className="ruleset-editor__field-help">{help}</span>
    </span>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
  </label>
);

const RulesetEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const isPremium = isPremiumTier(resolveTierFromUser(user));
  const [activeSection, setActiveSection] = useState('foundation');
  const [draft, setDraft] = useState(null);
  const [mode, setMode] = useState(id && id !== 'new' ? 'edit' : 'create');
  const [legalState, setLegalState] = useState({
    document: null,
    accepted: false,
    publicPublishingEnabled: false,
    publicPublishingOperationalReady: false
  });
  const [termsChecked, setTermsChecked] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [status, setStatus] = useState({ state: 'loading', message: 'Loading the rules contract…', issues: [] });

  const loadRuleset = useCallback(async () => {
    setStatus({ state: 'loading', message: 'Loading the rules contract…', issues: [] });
    try {
      const [documentsResponse, acceptancesResponse] = await Promise.all([
        apiFetch('/api/legal/documents'),
        apiFetch('/api/legal/acceptances', { requireAuth: true })
      ]);
      if (!documentsResponse.ok || !acceptancesResponse.ok) throw new Error('User-content terms could not be loaded.');
      const documentsPayload = await documentsResponse.json();
      const acceptances = await acceptancesResponse.json();
      const userContentDocument = documentsPayload.documents?.find((document) => document.type === 'user_content_terms');
      if (!userContentDocument) throw new Error('The current User Content Terms are unavailable.');
      const accepted = (Array.isArray(acceptances) ? acceptances : []).some((record) => (
        record.documentType === userContentDocument.type
        && record.documentVersion === userContentDocument.version
        && record.documentHash === userContentDocument.documentHash
      ));
      setLegalState({
        document: userContentDocument,
        accepted,
        publicPublishingEnabled: Boolean(documentsPayload.publicRulesetPublishingEnabled),
        publicPublishingOperationalReady: Boolean(documentsPayload.publicPublishingOperationalReady)
      });
      setTermsChecked(accepted);
      if (id && id !== 'new') {
        const response = await apiFetch(`/api/ruleset/${encodeURIComponent(id)}`, { requireAuth: true });
        if (!response.ok) throw new Error(`Ruleset could not be loaded (${response.status}).`);
        const loaded = await response.json();
        const stored = Boolean(loaded.packageId && loaded.ownerUserId);
        if (stored) {
          const versionsResponse = await apiFetch(`/api/ruleset/${encodeURIComponent(id)}/versions`, { requireAuth: true });
          if (!versionsResponse.ok) throw new Error(`Ruleset versions could not be loaded (${versionsResponse.status}).`);
          const versions = await versionsResponse.json();
          setVersionHistory(Array.isArray(versions) ? versions : []);
        } else {
          setVersionHistory([]);
        }
        setMode(stored ? 'edit' : 'create');
        setDraft({
          ...loaded,
          name: stored ? loaded.name : `${loaded.name} Variant`,
          version: stored ? nextPatchVersion(loaded.version) : loaded.version,
          visibility: stored ? loaded.visibility : 'private',
          provenance: stored ? {
            ...loaded.provenance,
            sourceClassification: loaded.provenance?.sourceClassification || '',
            intakeProvenance: loaded.provenance?.intakeProvenance || ''
          } : {
            sourceType: 'manual',
            sourceClassification: 'mixed',
            intakeProvenance: 'mixed',
            sourceName: loaded.name,
            sourceLicense: loaded.provenance?.sourceLicense || '',
            sourceAttribution: loaded.provenance?.sourceAttribution || '',
            userAttestedRights: false
          }
        });
      } else {
        setVersionHistory([]);
        const response = await apiFetch('/api/ruleset/presets', { requireAuth: true });
        if (!response.ok) throw new Error(`Built-in rules could not be loaded (${response.status}).`);
        const presets = await response.json();
        if (!Array.isArray(presets) || presets.length === 0) throw new Error('No built-in ruleset is registered.');
        const preset = presets[0];
        setMode('create');
        setDraft({
          ...preset,
          name: `${preset.name} Variant`,
          description: `A table variant based on ${preset.name}.`,
          visibility: 'private',
          provenance: {
            sourceType: 'manual',
            sourceClassification: 'mixed',
            intakeProvenance: 'mixed',
            sourceName: preset.name,
            sourceLicense: preset.provenance?.sourceLicense || '',
            sourceAttribution: preset.provenance?.sourceAttribution || '',
            userAttestedRights: false
          }
        });
      }
      setStatus({ state: 'ready', message: '', issues: [] });
    } catch (error) {
      setStatus({ state: 'error', message: error?.message || 'Ruleset could not be loaded.', issues: [] });
    }
  }, [id]);

  useEffect(() => {
    loadRuleset();
  }, [loadRuleset]);

  const controlsDisabled = !isPremium || status.state === 'saving';
  const overlayPriorities = useMemo(
    () => (draft?.overlays || []).map((overlay) => Number(overlay.priority)).filter(Number.isFinite),
    [draft]
  );

  const markDraftChanged = () => setStatus((current) => (
    current.state === 'saved' ? { state: 'ready', message: '', issues: [] } : current
  ));
  const update = (path, value) => {
    setDraft((current) => setAtPath(current, path, value));
    markDraftChanged();
  };
  const updateNumber = (path) => (event) => update(path, Number(event.target.value));
  const updateBoolean = (path) => (event) => update(path, event.target.checked);
  const handleTabKeyDown = (event, sectionIndex) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? SECTIONS.length - 1
        : (sectionIndex + (event.key === 'ArrowRight' ? 1 : -1) + SECTIONS.length) % SECTIONS.length;
    const nextSection = SECTIONS[nextIndex];
    setActiveSection(nextSection.id);
    document.getElementById(`ruleset-tab-${nextSection.id}`)?.focus();
  };

  const addOverlay = () => {
    const identifier = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}`;
    const nextPriority = overlayPriorities.length ? Math.max(...overlayPriorities) + 10 : 100;
    setDraft((current) => ({
      ...current,
      overlays: [
        ...current.overlays,
        {
          id: `house-rule-${identifier}`,
          name: 'New House Rule',
          version: '1.0.0',
          enabled: true,
          priority: nextPriority,
          overrides: {},
          modules: []
        }
      ]
    }));
    markDraftChanged();
  };

  const removeOverlay = (index) => {
    setDraft((current) => ({
      ...current,
      overlays: current.overlays.filter((_overlay, overlayIndex) => overlayIndex !== index)
    }));
    markDraftChanged();
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setStatus({ state: 'error', message: 'Ruleset JSON files must be 1 MB or smaller.', issues: [] });
      return;
    }
    if (!draft.provenance?.userAttestedRights) {
      setStatus({ state: 'error', message: 'Confirm that you have the rights needed to use the source before importing it.', issues: [] });
      return;
    }
    setStatus({ state: 'saving', message: 'Validating the imported rules contract…', issues: [] });
    try {
      const parsed = JSON.parse(await file.text());
      const candidate = parsed?.definition && typeof parsed.definition === 'object' ? parsed.definition : parsed;
      const provenance = {
        ...draft.provenance,
        sourceType: 'uploaded_json',
        intakeProvenance: 'provided',
        sourceName: file.name,
        userAttestedRights: true
      };
      const response = await apiFetch('/api/ruleset/import/preview', {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify({ definition: rulesetDefinitionPayload(candidate), provenance })
      });
      if (!response.ok) {
        const failure = await readErrorPayload(response);
        setStatus({ state: 'error', ...failure });
        return;
      }
      const preview = await response.json();
      setDraft((current) => ({
        ...current,
        ...preview.definition,
        provenance: preview.provenance,
        visibility: current.visibility
      }));
      setStatus({
        state: 'ready',
        message: `${file.name} passed the executable rules contract. The source file was processed locally and was not retained.`,
        issues: []
      });
    } catch (error) {
      setStatus({ state: 'error', message: error?.message || 'The selected file is not valid ruleset JSON.', issues: [] });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isPremium) {
      navigate(UPGRADE_ROUTE);
      return;
    }
    if (!termsChecked || !draft.provenance?.userAttestedRights) {
      setActiveSection('source-sharing');
      setStatus({
        state: 'error',
        message: 'Accept the current User Content Terms and confirm your source rights before saving.',
        issues: []
      });
      return;
    }
    setStatus({ state: 'saving', message: 'Saving the complete rules contract…', issues: [] });
    try {
      if (!legalState.accepted) {
        const acceptanceResponse = await apiFetch('/api/legal/acceptances', {
          method: 'POST',
          requireAuth: true,
          body: JSON.stringify({
            documentType: legalState.document.type,
            documentVersion: legalState.document.version,
            documentHash: legalState.document.documentHash,
            accepted: true,
            surface: 'ruleset_editor'
          })
        });
        if (!acceptanceResponse.ok) throw new Error('The User Content Terms acceptance could not be recorded.');
        setLegalState((current) => ({ ...current, accepted: true }));
      }
      const editId = mode === 'edit' ? id : null;
      const response = await apiFetch(editId ? `/api/ruleset/${encodeURIComponent(editId)}/versions` : '/api/ruleset', {
        method: 'POST',
        requireAuth: true,
        body: JSON.stringify(rulesetPayload(draft))
      });
      if (!response.ok) {
        const failure = await readErrorPayload(response);
        setStatus({ state: 'error', ...failure });
        return;
      }
      const saved = await response.json();
      setDraft({ ...saved, version: nextPatchVersion(saved.version) });
      setMode('edit');
      setVersionHistory((current) => [saved, ...current.filter((version) => version.rulesetVersionId !== saved.rulesetVersionId)]);
      setStatus({ state: 'saved', message: 'An immutable ruleset version was saved and is ready for BattleRoom selection.', issues: [] });
      if (!editId && saved.packageId) navigate(`/ruleset/${saved.packageId}`, { replace: true });
    } catch (error) {
      setStatus({ state: 'error', message: error?.message || 'Ruleset could not be saved.', issues: [] });
    }
  };

  if (status.state === 'loading' || !draft) {
    return (
      <main className="ruleset-editor ruleset-editor--centered" aria-live="polite">
        <section className="ruleset-editor__loading ui-fantasy-panel">
          <span className="ui-fantasy-kicker">Rules contract</span>
          <h1 className="ui-fantasy-title">Preparing the Rule Forge</h1>
          <p>{status.message}</p>
          {status.state === 'error' ? <button type="button" onClick={loadRuleset}>Try again</button> : null}
        </section>
      </main>
    );
  }

  return (
    <main className="ruleset-editor">
      <div className="ruleset-editor__shell">
        <header className="ruleset-editor__header">
          <div>
            <span className="ui-fantasy-kicker">Rules contract · schema {draft.schemaVersion}</span>
            <h1 className="ui-fantasy-title">{mode === 'edit' ? 'Create a New Ruleset Version' : 'Create a Ruleset'}</h1>
            <p>Every change is structured, validated, and recorded as base policy or an ordered overlay.</p>
          </div>
          <div className="ruleset-editor__header-actions">
            <Link className="ruleset-editor__button ruleset-editor__button--quiet" to="/dashboard">
              <ArrowLeft size={17} aria-hidden="true" /> Dashboard
            </Link>
            <span className="ruleset-editor__plan-state">
              <ShieldCheck size={17} aria-hidden="true" />
              {isPremium ? `${PREMIUM_PLAN_NAME} editing` : 'Core rules preview'}
            </span>
          </div>
        </header>

        {!isPremium ? (
          <section className="ruleset-editor__notice" aria-label="Upgrade required">
            <strong>Core rules remain playable.</strong>
            <span>Custom rulesets and overlays require {PREMIUM_PLAN_NAME}. You can inspect every rule before upgrading.</span>
            <Link to={UPGRADE_ROUTE}>Unlock ruleset editing</Link>
          </section>
        ) : null}

        <form className="ruleset-editor__form" onSubmit={handleSubmit}>
          <div className="ruleset-editor__tabs" role="tablist" aria-label="Ruleset sections">
            {SECTIONS.map((section, sectionIndex) => (
              <button
                key={section.id}
                id={`ruleset-tab-${section.id}`}
                type="button"
                role="tab"
                aria-selected={activeSection === section.id}
                aria-controls={`ruleset-panel-${section.id}`}
                tabIndex={activeSection === section.id ? 0 : -1}
                className={activeSection === section.id ? 'ruleset-editor__tab ruleset-editor__tab--active' : 'ruleset-editor__tab'}
                onClick={() => setActiveSection(section.id)}
                onKeyDown={(event) => handleTabKeyDown(event, sectionIndex)}
              >
                {section.label}
              </button>
            ))}
          </div>

          <section
            id={`ruleset-panel-${activeSection}`}
            className="ruleset-editor__panel"
            role="tabpanel"
            aria-labelledby={`ruleset-tab-${activeSection}`}
            tabIndex="0"
          >
            {activeSection === 'foundation' ? (
              <>
                <div className="ruleset-editor__section-heading">
                  <h2>Foundation</h2>
                  <p>Name the contract and declare which edition its base policy follows.</p>
                </div>
                <div className="ruleset-editor__field-grid">
                  <Field label="Ruleset name">
                    <input value={draft.name} disabled={controlsDisabled} minLength="3" maxLength="100" required onChange={(event) => update(['name'], event.target.value)} />
                  </Field>
                  <Field label="Contract version" help="Use semantic versioning, such as 1.0.0.">
                    <input value={draft.version} disabled={controlsDisabled} required pattern="\d+\.\d+\.\d+" onChange={(event) => update(['version'], event.target.value)} />
                  </Field>
                  <Field label="Edition">
                    <select value={draft.edition} disabled={controlsDisabled} onChange={(event) => update(['edition'], event.target.value)}>
                      <option value="2014">2014</option>
                      <option value="2024">2024</option>
                      <option value="custom">Custom</option>
                    </select>
                  </Field>
                  <Field label="Description">
                    <textarea value={draft.description} disabled={controlsDisabled} maxLength="500" rows="4" required onChange={(event) => update(['description'], event.target.value)} />
                  </Field>
                </div>
                <p className="ruleset-editor__module-note">Sharing and source rights are managed separately under Source &amp; Sharing. New rulesets remain private by default.</p>
              </>
            ) : null}

            {activeSection === 'attacks' ? (
              <>
                <div className="ruleset-editor__section-heading"><h2>Attack Resolution</h2><p>Global modifiers and critical-hit policy. Creature bonuses remain on each action.</p></div>
                <div className="ruleset-editor__field-grid ruleset-editor__field-grid--four">
                  <Field label="Attack-roll modifier"><input type="number" value={draft.baseRules.attack.attackRollModifier} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'attack', 'attackRollModifier'])} /></Field>
                  <Field label="Damage-roll modifier"><input type="number" value={draft.baseRules.attack.damageRollModifier} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'attack', 'damageRollModifier'])} /></Field>
                  <Field label="Critical threshold"><input type="number" min="1" max="20" value={draft.baseRules.attack.criticalHit.threshold} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'attack', 'criticalHit', 'threshold'])} /></Field>
                  <Field label="Critical dice multiplier"><input type="number" min="1" step="1" value={draft.baseRules.attack.criticalHit.damageDiceMultiplier} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'attack', 'criticalHit', 'damageDiceMultiplier'])} /></Field>
                </div>
                <div className="ruleset-editor__toggle-stack">
                  <ToggleField label="Natural 1 always misses" help="Applies before Armor Class comparison." checked={draft.baseRules.attack.naturalOneAlwaysMisses} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'attack', 'naturalOneAlwaysMisses'])} />
                  <ToggleField label="Natural 20 always hits" help="Applies before Armor Class comparison." checked={draft.baseRules.attack.naturalTwentyAlwaysHits} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'attack', 'naturalTwentyAlwaysHits'])} />
                </div>
              </>
            ) : null}

            {activeSection === 'magic' ? (
              <>
                <div className="ruleset-editor__section-heading"><h2>Spellcasting</h2><p>System policy only. Spell slots, save DCs, and attack bonuses remain on the caster.</p></div>
                <div className="ruleset-editor__toggle-stack">
                  <ToggleField label="Require spell slots" help="Leveled spells must spend an explicit slot." checked={draft.baseRules.spellcasting.requireSpellSlots} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'spellcasting', 'requireSpellSlots'])} />
                  <ToggleField label="Require prepared or known spells" help="The caster must own the spell in canonical data." checked={draft.baseRules.spellcasting.requirePreparedOrKnownSpell} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'spellcasting', 'requirePreparedOrKnownSpell'])} />
                  <ToggleField label="Require components" help="Component requirements remain enforceable." checked={draft.baseRules.spellcasting.requireComponents} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'spellcasting', 'requireComponents'])} />
                  <ToggleField label="Concentration enabled" help="Damage can trigger an explicit concentration decision." checked={draft.baseRules.spellcasting.concentration.enabled} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'spellcasting', 'concentration', 'enabled'])} />
                </div>
                <div className="ruleset-editor__field-grid ruleset-editor__field-grid--three">
                  <Field label="Base concentration DC"><input type="number" min="1" step="1" value={draft.baseRules.spellcasting.concentration.baseSaveDC} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'spellcasting', 'concentration', 'baseSaveDC'])} /></Field>
                  <Field label="Damage divisor"><input type="number" min="1" value={draft.baseRules.spellcasting.concentration.damageDivisor} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'spellcasting', 'concentration', 'damageDivisor'])} /></Field>
                  <Field label="DC rounding"><select value={draft.baseRules.spellcasting.concentration.rounding} disabled={controlsDisabled} onChange={(event) => update(['baseRules', 'spellcasting', 'concentration', 'rounding'], event.target.value)}><option value="floor">Round down</option><option value="ceil">Round up</option></select></Field>
                </div>
              </>
            ) : null}

            {activeSection === 'grapple-movement' ? (
              <>
                <div className="ruleset-editor__section-heading"><h2>Grapple & Movement</h2><p>Edition differences stay explicit and are consumed without parsing rule prose.</p></div>
                <div className="ruleset-editor__field-grid ruleset-editor__field-grid--three">
                  <Field label="Grapple resolution"><select value={draft.baseRules.grapple.resolution} disabled={controlsDisabled} onChange={(event) => update(['baseRules', 'grapple', 'resolution'], event.target.value)}><option value="contested_check">Contested check</option><option value="saving_throw">Saving throw</option></select></Field>
                  <Field label="Size difference limit"><input type="number" min="0" step="1" value={draft.baseRules.grapple.sizeLimitDifference} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'grapple', 'sizeLimitDifference'])} /></Field>
                  <Field label="Drag speed multiplier"><input type="number" min="0" max="1" step="0.1" value={draft.baseRules.grapple.dragSpeedMultiplier} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'grapple', 'dragSpeedMultiplier'])} /></Field>
                  <Field label="Difficult terrain cost"><input type="number" min="1" step="0.5" value={draft.baseRules.movement.difficultTerrainMultiplier} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'movement', 'difficultTerrainMultiplier'])} /></Field>
                  <Field label="Diagonal measurement"><select value={draft.baseRules.movement.diagonalMethod} disabled={controlsDisabled} onChange={(event) => update(['baseRules', 'movement', 'diagonalMethod'], event.target.value)}><option value="euclidean">Euclidean</option><option value="five_ten_five">5–10–5</option></select></Field>
                </div>
                <ToggleField label="Forced movement provokes opportunity attacks" help="The engine uses this exact policy during forced movement." checked={draft.baseRules.movement.forcedMovementProvokesOpportunityAttacks} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'movement', 'forcedMovementProvokesOpportunityAttacks'])} />
              </>
            ) : null}

            {activeSection === 'rests' ? (
              <>
                <div className="ruleset-editor__section-heading"><h2>Resting</h2><p>Rest timing and recovery are ruleset policy, including whether a short rest can spend Hit Dice.</p></div>
                <div className="ruleset-editor__field-grid ruleset-editor__field-grid--three">
                  <Field label="Short rest (minutes)"><input type="number" min="1" value={draft.baseRules.rests.shortRestMinutes} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'rests', 'shortRestMinutes'])} /></Field>
                  <Field label="Long rest (hours)"><input type="number" min="1" value={draft.baseRules.rests.longRestHours} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'rests', 'longRestHours'])} /></Field>
                  <Field label="Hit Die recovery divisor" help="2 means recover half of maximum Hit Dice."><input type="number" min="1" value={draft.baseRules.rests.longRestHitDiceRecoveryDivisor} disabled={controlsDisabled} onChange={updateNumber(['baseRules', 'rests', 'longRestHitDiceRecoveryDivisor'])} /></Field>
                </div>
                <div className="ruleset-editor__toggle-stack">
                  <ToggleField label="Short rest allows Hit Dice" help="Characters may spend their own available Hit Dice." checked={draft.baseRules.rests.shortRestAllowsHitDice} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'rests', 'shortRestAllowsHitDice'])} />
                  <ToggleField label="Long rest restores all Hit Points" help="Disable for recovery variants that heal elsewhere." checked={draft.baseRules.rests.longRestRestoresAllHitPoints} disabled={controlsDisabled} onChange={updateBoolean(['baseRules', 'rests', 'longRestRestoresAllHitPoints'])} />
                </div>
              </>
            ) : null}

            {activeSection === 'overlays' ? (
              <>
                <div className="ruleset-editor__section-heading ruleset-editor__section-heading--actions">
                  <div><h2>Ordered Rule Overlays</h2><p>Enabled overlays apply from lowest priority to highest, then by stable ID.</p></div>
                  <button type="button" className="ruleset-editor__button ruleset-editor__button--secondary" disabled={controlsDisabled} onClick={addOverlay}><Plus size={17} aria-hidden="true" /> Add overlay</button>
                </div>
                {draft.overlays.length === 0 ? (
                  <div className="ruleset-editor__empty"><strong>No overlays yet.</strong><span>The base policy above runs unchanged. Add an overlay for named house rules or campaign variants.</span></div>
                ) : (
                  <div className="ruleset-editor__overlay-list">
                    {draft.overlays.map((overlay, index) => (
                      <article className="ruleset-editor__overlay" key={overlay.id}>
                        <div className="ruleset-editor__overlay-heading">
                          <div>
                            <span className="ruleset-editor__overlay-order">Priority {overlay.priority}</span>
                            <h3>{overlay.name}</h3>
                          </div>
                          <button type="button" className="ruleset-editor__button ruleset-editor__button--destructive" disabled={controlsDisabled} onClick={() => removeOverlay(index)}><Trash2 size={16} aria-hidden="true" /> Remove</button>
                        </div>
                        <div className="ruleset-editor__field-grid ruleset-editor__field-grid--four">
                          <Field label="Overlay name"><input value={overlay.name} disabled={controlsDisabled} onChange={(event) => update(['overlays', index, 'name'], event.target.value)} /></Field>
                          <Field label="Version"><input value={overlay.version} disabled={controlsDisabled} pattern="\d+\.\d+\.\d+" onChange={(event) => update(['overlays', index, 'version'], event.target.value)} /></Field>
                          <Field label="Priority"><input type="number" step="1" value={overlay.priority} disabled={controlsDisabled} onChange={updateNumber(['overlays', index, 'priority'])} /></Field>
                          <ToggleField label="Enabled" help="Disabled overlays remain saved but do not execute." checked={overlay.enabled} disabled={controlsDisabled} onChange={updateBoolean(['overlays', index, 'enabled'])} />
                        </div>
                        <div className="ruleset-editor__subheading">Optional overrides</div>
                        <div className="ruleset-editor__field-grid ruleset-editor__field-grid--four">
                          <Field label="Critical threshold"><input type="number" min="1" max="20" placeholder="Inherit" value={getAtPath(overlay, ['overrides', 'attack', 'criticalHit', 'threshold']) ?? ''} disabled={controlsDisabled} onChange={(event) => update(['overlays', index, 'overrides', 'attack', 'criticalHit', 'threshold'], event.target.value === '' ? undefined : Number(event.target.value))} /></Field>
                          <Field label="Critical dice multiplier"><input type="number" min="1" placeholder="Inherit" value={getAtPath(overlay, ['overrides', 'attack', 'criticalHit', 'damageDiceMultiplier']) ?? ''} disabled={controlsDisabled} onChange={(event) => update(['overlays', index, 'overrides', 'attack', 'criticalHit', 'damageDiceMultiplier'], event.target.value === '' ? undefined : Number(event.target.value))} /></Field>
                          <Field label="Short rest minutes"><input type="number" min="1" placeholder="Inherit" value={getAtPath(overlay, ['overrides', 'rests', 'shortRestMinutes']) ?? ''} disabled={controlsDisabled} onChange={(event) => update(['overlays', index, 'overrides', 'rests', 'shortRestMinutes'], event.target.value === '' ? undefined : Number(event.target.value))} /></Field>
                          <Field label="Difficult terrain cost"><input type="number" min="1" step="0.5" placeholder="Inherit" value={getAtPath(overlay, ['overrides', 'movement', 'difficultTerrainMultiplier']) ?? ''} disabled={controlsDisabled} onChange={(event) => update(['overlays', index, 'overrides', 'movement', 'difficultTerrainMultiplier'], event.target.value === '' ? undefined : Number(event.target.value))} /></Field>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
                <p className="ruleset-editor__module-note">Schema-backed modules remain unavailable until their executable handler is registered. Enabled unknown modules are rejected by design.</p>
              </>
            ) : null}

            {activeSection === 'source-sharing' ? (
              <>
                <div className="ruleset-editor__section-heading">
                  <h2>Source &amp; Sharing</h2>
                  <p>Record where the mechanics came from, confirm your rights, and choose who can use this exact package.</p>
                </div>

                <div className="ruleset-editor__source-grid">
                  <section className="ruleset-editor__source-card" aria-labelledby="ruleset-source-heading">
                    <div>
                      <span className="ruleset-editor__overlay-order">Import</span>
                      <h3 id="ruleset-source-heading">Ruleset JSON</h3>
                      <p>Import a complete schema-version 1 definition. Validation happens before the draft changes; the source file is not retained.</p>
                    </div>
                    <label className={`ruleset-editor__button ruleset-editor__button--secondary ${controlsDisabled ? 'ruleset-editor__button--disabled' : ''}`}>
                      <FileJson size={17} aria-hidden="true" /> Choose JSON file
                      <input
                        className="ruleset-editor__file-input"
                        type="file"
                        accept=".json,application/json"
                        disabled={controlsDisabled}
                        onChange={handleImport}
                      />
                    </label>
                  </section>

                  <section className="ruleset-editor__source-card" aria-labelledby="ruleset-sharing-heading">
                    <div>
                      <span className="ruleset-editor__overlay-order">Access</span>
                      <h3 id="ruleset-sharing-heading">Package visibility</h3>
                      <p>Private is the safe default. Campaign packages require an explicit BattleRoom grant. Public publishing remains unavailable until the operational review is complete.</p>
                    </div>
                    <Field label="Who can use this package?">
                      <select value={draft.visibility} disabled={controlsDisabled} onChange={(event) => update(['visibility'], event.target.value)}>
                        <option value="private">Only me</option>
                        <option value="campaign">Specific BattleRooms I grant</option>
                        <option value="public" disabled={!legalState.publicPublishingEnabled || !legalState.publicPublishingOperationalReady}>Public catalog</option>
                      </select>
                    </Field>
                  </section>
                </div>

                <div className="ruleset-editor__field-grid ruleset-editor__field-grid--three">
                  <Field label="Source basis" help="Choose the basis you understand applies; private use does not require VCS to independently verify it.">
                    <select value={draft.provenance?.sourceClassification || ''} disabled={controlsDisabled} required onChange={(event) => update(['provenance', 'sourceClassification'], event.target.value)}>
                      <option value="" disabled>Choose source basis</option>
                      <option value="original">My original rules</option>
                      <option value="srd-5.1-cc-by">SRD 5.1 · CC BY 4.0</option>
                      <option value="srd-5.2.1-cc-by">SRD 5.2.1 · CC BY 4.0</option>
                      <option value="licensed">Other licensed material</option>
                      <option value="fan-content">Fan-content policy</option>
                      <option value="user-provided">User-provided material</option>
                      <option value="mixed">Mixed sources</option>
                    </select>
                  </Field>
                  <Field label="Source name" help="For example, your campaign guide or an openly licensed rules document.">
                    <input value={draft.provenance?.sourceName || ''} disabled={controlsDisabled} maxLength="200" onChange={(event) => update(['provenance', 'sourceName'], event.target.value)} />
                  </Field>
                  <Field label="Source URL" help="Optional public license or attribution link.">
                    <input type="url" value={draft.provenance?.sourceUrl || ''} disabled={controlsDisabled} onChange={(event) => update(['provenance', 'sourceUrl'], event.target.value)} />
                  </Field>
                  <Field label="License" help="For example, CC BY 4.0, or Private original material.">
                    <input value={draft.provenance?.sourceLicense || ''} disabled={controlsDisabled} maxLength="200" onChange={(event) => update(['provenance', 'sourceLicense'], event.target.value)} />
                  </Field>
                  <Field label="Attribution" help="Keep required credit with the version record.">
                    <textarea value={draft.provenance?.sourceAttribution || ''} disabled={controlsDisabled} maxLength="2000" rows="4" onChange={(event) => update(['provenance', 'sourceAttribution'], event.target.value)} />
                  </Field>
                </div>

                <div className="ruleset-editor__agreement-stack">
                  <ToggleField
                    label="I have the rights needed for this use"
                    help="You are responsible for ensuring the source and the selected sharing scope comply with applicable licenses, fan-content terms, copyright, and trademark law. A disclaimer does not create permission."
                    checked={Boolean(draft.provenance?.userAttestedRights)}
                    disabled={controlsDisabled}
                    onChange={updateBoolean(['provenance', 'userAttestedRights'])}
                  />
                  <ToggleField
                    label={`I accept the ${legalState.document?.title || 'User Content Terms'}`}
                    help={`Version ${legalState.document?.version || 'unavailable'}. You keep ownership and grant only the license needed to operate the service. Reported content may be restricted while reviewed.`}
                    checked={termsChecked}
                    disabled={controlsDisabled || legalState.accepted}
                    onChange={(event) => setTermsChecked(event.target.checked)}
                  />
                  {legalState.document?.summary?.length ? (
                    <details className="ruleset-editor__terms-summary" open={!legalState.accepted}>
                      <summary>Review the exact versioned summary being accepted</summary>
                      <ul>{legalState.document.summary.map((item) => <li key={item}>{item}</li>)}</ul>
                    </details>
                  ) : null}
                  <p className="ruleset-editor__terms-link">
                    Review the policy summary and source licenses on the <Link to="/legal/sources">Legal &amp; Sources page</Link>. These product terms are awaiting external legal review and are not legal advice.
                  </p>
                </div>

                {mode === 'edit' && versionHistory.length > 0 ? (
                  <section className="ruleset-editor__version-history" aria-labelledby="ruleset-version-history-heading">
                    <div className="ruleset-editor__section-heading">
                      <h3 id="ruleset-version-history-heading">Immutable version history</h3>
                      <p>Saving creates a child version. Existing BattleRooms remain pinned to the version and hash they started with.</p>
                    </div>
                    <ol>
                      {versionHistory.map((version) => (
                        <li key={version.rulesetVersionId}>
                          <span><strong>{version.version}</strong> · {version.edition}</span>
                          <code title={version.definitionHash}>{version.definitionHash?.slice(0, 20)}…</code>
                          <span>{version.createdAt ? new Date(version.createdAt).toLocaleString() : 'Creation time unavailable'}</span>
                        </li>
                      ))}
                    </ol>
                  </section>
                ) : null}
              </>
            ) : null}
          </section>

          {status.state === 'error' || status.state === 'saved' || (status.state === 'ready' && status.message) ? (
            <section className={`ruleset-editor__feedback ruleset-editor__feedback--${status.state}`} role={status.state === 'error' ? 'alert' : 'status'} aria-live="polite">
              <strong>{status.state === 'saved' ? 'Ready for play' : status.state === 'ready' ? 'Draft updated' : 'Ruleset needs attention'}</strong>
              <span>{status.message}</span>
              {status.issues?.length ? (
                <details><summary>Show contract details</summary><ul>{status.issues.map((issue, index) => <li key={`${issue.path}-${index}`}><code>{issue.path}</code>: {issue.message || issue.expected}</li>)}</ul></details>
              ) : null}
            </section>
          ) : null}

          <footer className="ruleset-editor__footer">
            <button type="button" className="ruleset-editor__button ruleset-editor__button--quiet" onClick={() => navigate('/dashboard')}>Cancel</button>
            {isPremium ? (
              <button type="submit" className="ruleset-editor__button ruleset-editor__button--primary" disabled={status.state === 'saving'}>
                <Save size={17} aria-hidden="true" /> {status.state === 'saving' ? 'Saving ruleset…' : 'Save ruleset'}
              </button>
            ) : (
              <Link className="ruleset-editor__button ruleset-editor__button--primary" to={UPGRADE_ROUTE}>Unlock {PREMIUM_PLAN_NAME}</Link>
            )}
          </footer>
        </form>
      </div>
    </main>
  );
};

export default RulesetEditor;
export { rulesetPayload, setAtPath };
